import Seo from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import { LegalBody, LegalSection } from "@/components/LegalPage";
import { SITE } from "@/lib/site";

export default function PrivacyPolicy() {
  return (
    <>
      <Seo
        title="Privacy Policy | AutoNeova"
        description="How AutoNeova handles visitor information on its corporate website, including hosting logs, email inquiries, and purchases completed through Amazon."
        path="/privacy-policy"
      />
      <PageHeader
        testId="privacy-page-header"
        eyebrow="Legal"
        title="Privacy Policy"
        intro={`Effective Date: ${SITE.effectiveDate}`}
      />
      <LegalBody testId="privacy-page-content">
        <p className="text-base leading-relaxed text-[#263238]">
          This Privacy Policy explains how AutoNeova handles information in connection with this
          website. This website is an informational corporate website.
        </p>

        <LegalSection heading="1. Purchases Are Completed Through Amazon">
          <p>
            This website does not directly process purchases. Purchases of AutoNeova products are
            completed through Amazon and are subject to Amazon's applicable terms and privacy
            practices. Payment, shipping, and account information entered on Amazon is handled by
            Amazon, not by this website.
          </p>
        </LegalSection>

        <LegalSection heading="2. Information Collected Through This Website">
          <p>
            This website may collect basic technical information through standard hosting logs. This
            can include the requesting IP address, browser type, referring page, requested pages, and
            the date and time of a request. This information is used to operate and secure the
            website.
          </p>
        </LegalSection>

        <LegalSection heading="3. Email Inquiries">
          <p>
            If a visitor contacts AutoNeova by email, the information supplied may be used to respond
            to that inquiry and to keep a record of the correspondence. We ask that visitors do not
            send sensitive personal or payment information by email.
          </p>
        </LegalSection>

        <LegalSection heading="4. Sale of Personal Information">
          <p>AutoNeova does not sell personal information.</p>
        </LegalSection>

        <LegalSection heading="5. Third-Party Services">
          <p>
            This website links to Amazon. When a visitor follows a link to Amazon, that visit is
            governed by Amazon's own terms and privacy practices. AutoNeova does not control those
            practices.
          </p>
        </LegalSection>

        <LegalSection heading="6. Data Retention">
          <p>
            Hosting logs and email correspondence are retained only for as long as reasonably needed
            for the purposes described above, or as required for record-keeping.
          </p>
        </LegalSection>

        <LegalSection heading="7. Changes to This Policy">
          <p>
            This Privacy Policy may be updated from time to time. Any updated version will be posted
            on this page with a revised effective date.
          </p>
        </LegalSection>

        <LegalSection heading="8. Privacy Questions">
          <p>
            Visitors may contact AutoNeova regarding privacy questions by email at{" "}
            <a
              href={`mailto:${SITE.email}`}
              data-testid="privacy-email-link"
              className="font-semibold text-[#1F6FEB] underline decoration-[#1F6FEB]/30 underline-offset-4 hover:decoration-[#1F6FEB]"
            >
              {SITE.email}
            </a>
            .
          </p>
          <div
            data-testid="privacy-company-info"
            className="rounded-lg border border-[#0B1F33]/10 bg-[#F4F6F8] p-6 text-sm leading-relaxed text-[#263238]"
          >
            <p className="font-semibold text-[#0B1F33]">{SITE.legalName}</p>
            <p>Brand: {SITE.brand}</p>
            <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-[#263238]/60">
              Registered Business Address
            </p>
            <address className="not-italic">
              {SITE.address.line1}
              <br />
              {`${SITE.address.city}, ${SITE.address.state} ${SITE.address.postalCode}`}
              <br />
              {SITE.address.country}
            </address>
            <p className="mt-2">
              Email:{" "}
              <a
                href={`mailto:${SITE.email}`}
                className="font-medium text-[#1F6FEB] underline decoration-[#1F6FEB]/30 underline-offset-4 hover:decoration-[#1F6FEB]"
              >
                {SITE.email}
              </a>
            </p>
            <p>Website: {SITE.website}</p>
            <p>
              Amazon sales page:{" "}
              <a
                href={SITE.amazonStore}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="privacy-amazon-link"
                className="break-all font-medium text-[#1F6FEB] underline decoration-[#1F6FEB]/30 underline-offset-4 hover:decoration-[#1F6FEB]"
              >
                {SITE.amazonStore}
              </a>
            </p>
          </div>
        </LegalSection>
      </LegalBody>
    </>
  );
}
